import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { Link } from 'react-router';

import type { PillOption } from '~/components/common/PillRadioGroup';
import { KNOWLEDGE_KIND_STYLE } from '~/components/tasks/taskStyle';
import type { KnowledgeEntryKindEnum, KnowledgeEntrySourceEnum } from '~/data/generated/model';

export const KNOWLEDGE_KINDS: KnowledgeEntryKindEnum[] = ['note', 'decision', 'commitment'];

export const KNOWLEDGE_KIND_LABELS: Record<KnowledgeEntryKindEnum, string> = {
  note: 'Note',
  decision: 'Decision',
  commitment: 'Commitment',
};

export const KNOWLEDGE_KIND_OPTIONS: PillOption<KnowledgeEntryKindEnum>[] = KNOWLEDGE_KINDS.map(
  (kind) => ({
    value: kind,
    label: KNOWLEDGE_KIND_LABELS[kind],
    style:
      kind === 'note'
        ? { bg: '#eceff3', fg: '#1b1f24', border: '#cfd6e0' }
        : KNOWLEDGE_KIND_STYLE[kind],
  }),
);

const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: 12,
  lineHeight: 1.5,
  p: '2px 9px',
  borderRadius: '11px',
  whiteSpace: 'nowrap',
} as const;

export function KnowledgeKindChip({ kind }: { kind: KnowledgeEntryKindEnum }) {
  const style = KNOWLEDGE_KIND_STYLE[kind];
  return (
    <Box
      component="span"
      sx={{
        ...pillSx,
        fontWeight: 500,
        border: `1px solid ${style.border}`,
        bgcolor: style.bg,
        color: style.fg,
      }}
    >
      {KNOWLEDGE_KIND_LABELS[kind]}
    </Box>
  );
}

const iconSx = { fontSize: 14 };
const sourcePillSx = { ...pillSx, border: '1px solid #cfd6e0', color: '#333' };

export function KnowledgeSourceBadge({
  source,
  task,
  linkTask = false,
}: {
  source: KnowledgeEntrySourceEnum;
  task?: { id: string; displayId: string } | null;
  linkTask?: boolean;
}) {
  if (source === 'task') {
    const label = (
      <>
        <TaskAltIcon sx={iconSx} />
        {task ? `From task · ${task.displayId}` : 'From task'}
      </>
    );
    if (!task || !linkTask) {
      return (
        <Box component="span" sx={sourcePillSx}>
          {label}
        </Box>
      );
    }
    return (
      <Box
        component={Link}
        to={`/tasks/${task.id}`}
        sx={{ ...sourcePillSx, color: '#1f56c8', textDecoration: 'none', cursor: 'pointer' }}
      >
        {label}
      </Box>
    );
  }

  return (
    <Box component="span" sx={sourcePillSx}>
      {source === 'manual' ? <EditNoteIcon sx={iconSx} /> : <AutoAwesomeIcon sx={iconSx} />}
      {source === 'manual' ? 'Manual' : 'Lift AI Assistant'}
    </Box>
  );
}

export const formatKnowledgeDate = (iso: string) => {
  const date = parseISO(iso);
  return isValid(date) ? format(date, 'MMM d, yyyy') : '';
};

export function knowledgeSavedMessage({
  saved,
  superseded,
  followUps,
}: {
  saved: number;
  superseded: number;
  followUps: number;
}): string {
  return [
    `${saved} knowledge entr${saved === 1 ? 'y' : 'ies'} saved`,
    superseded ? `${superseded} marked superseded` : '',
    followUps ? `${followUps} follow-up task${followUps === 1 ? '' : 's'} created` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
